
import LockIcon from '@mui/icons-material/Lock'
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  Grid,
  MenuItem,
  SelectChangeEvent,
  Stack,
  TextField,
  Typography,
  useTheme,
} from '@mui/material'
import { DateRangePicker, LocalizationProvider } from '@mui/x-date-pickers-pro'
import { AdapterDayjs } from '@mui/x-date-pickers-pro/AdapterDayjs'
import { useEntitiesFromSearch } from 'api/search'
import { useUserItems } from 'api/aws/users/useUserItems'
import dayjs from 'dayjs'
import React, { useEffect, useMemo, useState } from 'react'
import useRawUpload from '../../api/aws/tasks/useRawUpload'
import { useSubmitTask } from '../../api/aws/tasks/useSubmitTask'
import { useCreateTask } from '../../api/aws/useCreateTask'
import { useUser } from '../../api/aws/useUser'
import { useUsersById } from '../../api/customHooks/useUsersById'
import { useCreateGoogleFolder } from '../../api/google/useCreateGoogleFolder'
import { useActiveUser } from '../../api/slack/useActiveUser'
import { useValidationContext } from '../../context/ValidationProvider/ValidationProvider'
import { BoxScroller } from '../../styles/elements/styleElements'
import { dialogStyles as formDialogStyles } from '../../styles/utilities'
import { normalizeDate } from '../../utils/dateUtils'
import OnboardTeamAutocomplete from '../DataGrid/components/OnboardTeamAutocomplete'
import DrawerLoading from '../Loading/DrawerLoading'
import { NewFileAttachments } from '../TaskDrawer/NewFileAttachments'
import CreateTaskHeader from '../Tasks/CreateTaskHeader'
import TaskRichText from '../Tasks/TaskRichText'
import { calculateTaskDays, formatDateString, workday } from '../Tasks/taskUtil'
import { fullBrandList, logoTaskList, photoVideoTravelTaskList, premiumWebsiteList } from './clientTasklistMaps'
import FieldComponent from './FieldComponent'
import FormAutocomplete from './FormAutocomplete'
import FormTypeSelect from './FormTypeSelect'
import SubtaskStep from './SubtaskStep'
import { toCalendarDate } from 'utils/formatDayjsDateTime'
import {
  CreativeFormData,
  CreativeIntakeProps,
  creativeTaskTypeLabels,
  CreativeTypeOptions,
  existingClientOptions,
  FieldConfig,
  FieldType,
  FormConfig,
  initialFormData,
  requestedByOptions,
  requestTypeOptions,
  User,
} from './types'

interface OptionType {
  id: string
  name?: string // For organizations
  dba?: string // DBA field if needed
  firstName?: string // For users
  lastName?: string // For users
  image_512?: string // For avatars
  org_uuid?: string
  AM: User[]
  DMS: User[]
  website: string
  deal_uuid: string
  deals: object[]
  organization: object | null
  subfolderIds?: { tasksFolderId?: string; creativeTasksFolderId?: string; [key: string]: any }
  googleDriveFolderId?: string
}
const CreativeIntakeForm: React.FC<CreativeIntakeProps & { prefillData?: Partial<CreativeFormData> }> = ({
  open,
  onClose,
  prefillData,
}) => {
  const { data: users, isLoading: isUsersLoading, isError: isUsersError } = useUserItems()
  console.log('users', users)

  const [selectedOrganization, setSelectedOrganization] = useState<OptionType | null>(null)
  const [selectedOrgId, setSelectedOrgId] = useState<string | null>(null)
  const { organizations } = useEntitiesFromSearch()
  const organization = organizations?.find(o => o?.org_uuid === selectedOrgId)
  const [taskName, setTaskName] = useState('')

  const [selectedFiles, setSelectedFiles] = useState<File[]>([])
  const { mutateAsync: rawUpload } = useRawUpload()

  const userIds = useMemo(() => {
    if (!organization) return []

    // Combine AM and DMS arrays, ensuring unique valid IDs
    const combinedIds = [...(organization.AM ?? []), ...(organization.DMS ?? [])]

    return Array.from(new Set(combinedIds.filter(Boolean))) // Remove duplicates & falsy values
  }, [organization])
  const { activeUser, isActiveUserLoading } = useActiveUser()
  const { id: activeUserId } = activeUser?.user ?? {}
  const { user: currentUser, isUserLoading: isCurrentUserLoading } = useUser(activeUserId)
  const { users: taskAssignees, isUsersByIdLoading: isTaskAssigneesLoading } = useUsersById(userIds ?? [])
  const [selectedDeal, setSelectedDeal] = useState<OptionType | null>(null)
  const [orgDeals, setOrgDeals] = useState<object[]>([])
  const { mutate: createTaskFolder } = useCreateGoogleFolder()
  const [formData, setFormData] = useState<CreativeFormData>({
    ...initialFormData,
    ...prefillData,
    type: prefillData?.type || '',
    assigner: (currentUser
      ? {
          user_uuid: currentUser.user_uuid || '',
          id: currentUser.id || '',
          firstName: currentUser.firstName || '',
          lastName: currentUser.lastName || '',
          name: currentUser.name || '',
          image_512: currentUser.image_512 || '',
          role: currentUser.role || '',
          title: currentUser.title || '',
        }
      : {}) as User,
    requestType: prefillData?.requestType ?? '',
  })
  const [isCreating, setIsCreating] = useState(false)
  const { mutate: createTask } = useCreateTask()
  const { submitNewTask } = useSubmitTask()
  const { validateCreativeIntake, creativeIntakeErrors } = useValidationContext()
  const [isFormValid, setIsFormValid] = useState<boolean>(false)
  const theme = useTheme()
  ///////////////////////////////////////////////////////////////////////
  ///////////////////////////////////////////////////////////////////////
  ///////////////////////////////////////////////////////////////////////
  const [subtaskOptions, setSubtaskOptions] = useState<Record<string, FieldConfig>>({})
  const [selectedSubtaskKeys, setSelectedSubtaskKeys] = useState<string[]>([])
  const [subtaskDescriptions, setSubtaskDescriptions] = useState<Record<string, string>>({})
  const [step, setStep] = useState<'form' | 'subtasks'>('form')
  const [showSubtaskStep, setShowSubtaskStep] = useState(false)

  useEffect(() => {
    if (!formData.type) return

    const type = formData.type

    const checklistMap = {
      fullBrandBuild: Object.fromEntries(fullBrandList.map(item => [item.key, { label: item.name }])),
      logoDesign: Object.fromEntries(logoTaskList.map(item => [item.key, { label: item.name }])),
      website: Object.fromEntries(premiumWebsiteList.map(item => [item.key, { label: item.name }])),
      photography: Object.fromEntries(photoVideoTravelTaskList.map(item => [item.key, { label: item.name }])),
      printDesign: Object.fromEntries(logoTaskList.map(item => [item.key, { label: item.name }])),
      websiteDesignOverview: Object.fromEntries(premiumWebsiteList.map(item => [item.key, { label: item.name }])),
      websiteStructure: Object.fromEntries(premiumWebsiteList.map(item => [item.key, { label: item.name }])),
      video: Object.fromEntries(photoVideoTravelTaskList.map(item => [item.key, { label: item.name }])),
    }

    const checklist = checklistMap[type] || {}
    setSubtaskOptions(checklist)
    setSelectedSubtaskKeys(Object.keys(checklist)) // All checked by default
  }, [formData.type])

  ///////////////////////////////////////////////////////////////////////
  ///////////////////////////////////////////////////////////////////////
  ///////////////////////////////////////////////////////////////////////
  ///////////////////////////////////////////////////////////////////////
  useEffect(() => {
    if (prefillData?.orgId && Array.isArray(organizations)) {
      console.log('prefillData', prefillData)
      const matched = organizations.find(org => org.org_uuid === prefillData.orgId)
      if (matched) {
        setSelectedOrganization(matched as unknown as OptionType)
      }
    }
  }, [prefillData?.orgId, organizations])

  ///////////////////////////////////////////////////////////////////////
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }))
  }
  useEffect(() => {
    // ⛔ Don't override if prefillData.name is already present
    if (formData?.type) {
      setTaskName(handleFormatTypesString(formData.type))
    }
  }, [organization, formData?.type, prefillData?.type])

  useEffect(() => {
    if (taskName) {
      setFormData(prev => ({
        ...prev,
        name: taskName,
      }))
    }
  }, [taskName])

  useEffect(() => {
    if (organization) {
      setFormData(prev => ({
        ...prev, // Preserve all other existing fields
        assignees: taskAssignees
          ? (taskAssignees.map(user => ({
              user_uuid: user.user_uuid || '',
              id: user.id || '',
              firstName: user.firstName || '',
              lastName: user.lastName || '',
              name: user.name || '',
              image_512: user.image_512 || '',
              role: user.role || '',
              title: user.title || '',
            })) as User[])
          : prev.assignees, // Ensure we don't override with `undefined`
        assigner: currentUser
          ? ({
              user_uuid: currentUser.user_uuid || '',
              id: currentUser.id || '',
              firstName: currentUser.firstName || '',
              lastName: currentUser.lastName || '',
              name: currentUser.name || '',
              image_512: currentUser.image_512 || '',
              role: currentUser.role || '',
              title: currentUser.title || '',
            } as User)
          : prev.assigner, // Preserve the previous value if currentUser is null
      }))
    }
  }, [organization]) // Added dependencies to prevent stale values

  const handleChange = (event: { target: { name: string; value: unknown } }) => {
    const { name, value } = event.target
    const selectedRequestType = formData.type as keyof CreativeTypeOptions
    const fieldType = selectedRequestType ? FormConfig[selectedRequestType]?.[name]?.type : undefined

    setFormData(prev => ({
      ...prev,
      [name]: fieldType === FieldType.MULTISELECT ? (Array.isArray(value) ? value : [value]) : value,
    }))
  }
  const handleUserChange = (
    role: string,
    selectedUsers: User[]
  ) => {
    const key = role.toLowerCase() // ensure it matches the formData keys
    const formattedUsers = selectedUsers.map(user => ({
      user_uuid: user?.user_uuid || user?.id,
      firstName: user.firstName,
      lastName: user.lastName,
      name: user.name,
      image_512: user.image_512,
      role: user.role,
    }))

    setFormData(prev => ({
      ...prev,
      [key]: formattedUsers,
    }))
  }

  const handleAddtlNotesChange = (newValue: string) => {
    setFormData(prev => ({ ...prev, addtlNotes: newValue }))
  }
  const handleOrganizationChange = (selectedOrg: any) => {
    setSelectedOrganization(selectedOrg)
    setSelectedOrgId(selectedOrg?.org_uuid || null)
    const newOrgDeals = selectedOrg?.deals
    setOrgDeals(newOrgDeals || [])
    setFormData(prev => ({
      ...prev,
      orgId: selectedOrg?.org_uuid || '',
      website: selectedOrg?.website || '',
      tasksFolderId: selectedOrg?.subfolderIds?.tasksFolderId,
      creativeTasksFolderId: selectedOrg?.subfolderIds?.creativeTasksFolderId,
      googleDriveFolderId: selectedOrg?.googleDriveFolderId,
    }))
  }
  const handleFormatTypesString = (type: string) => {
    switch (type) {
      case 'socialAd':
        return 'Social Ads'
      case 'displayAds':
        return 'Display Ads'
      case 'organicSocial':
        return 'Organic Social'
      case 'photography':
        return 'Photography'
      case 'printDesign':
        return 'Print Design'
      case 'website':
        return 'Website'
      case 'video':
        return 'Video'
      case 'logoDesign':
        return 'Logo Design'
      case 'brandDesign':
        return 'Brand Design'
      default:
        return ''
    }
  }
  const handleRequestTypeChange = (e: React.ChangeEvent<HTMLInputElement> | SelectChangeEvent<string>) => {
    const selectedType = e.target.value as keyof CreativeTypeOptions
    const formattedStrings = handleFormatTypesString(selectedType)
    console.log('formattedStrings', formattedStrings)
    setFormData(prev => ({
      ...prev,
      name: `${taskName}${creativeTaskTypeLabels[selectedType]}`,
      type: selectedType,
      taskType: selectedType,
      website: prev?.website || '',
      AM: prev?.AM || [],
      creativeLead: prev?.creativeLead || [],
      DMS: prev?.DMS || [],
      startDate: prev?.startDate || '',
      endDate: prev?.endDate || '',
      addtlNotes: prev?.addtlNotes || '',
      assignees: prev?.assignees || [],
      creativeStatus: prev?.creativeStatus || 'Staging',
    }))
  }

  const fieldTypeOrder = [
    FieldType.TEXT,
    FieldType.SELECT,
    FieldType.MULTISELECT,
    FieldType.CHECKBOX,
    FieldType.NUMBER,
    FieldType.COLOR_PICKER,
    FieldType.DATE,
    FieldType.TEXTAREA, // Put textareas at the bottom
  ]

  const { fields } = useMemo(() => {
    if (!formData.type) {
      return { fields: [] }
    }

    const selectedType = formData.type as keyof CreativeTypeOptions
    return {
      fields: FormConfig[selectedType] ? Object.entries(FormConfig[selectedType]) : [],
    }
  }, [formData.type])

  useEffect(() => {
    const validateForm = async () => {
      if (formData) {
        const { isValid } = await validateCreativeIntake(formData) // Await the validation
        setIsFormValid(isValid)
      }
    }

    validateForm() // Call the async function
  }, [formData, formData?.type])

  const handleSubmitNewTask = async () => {
    setIsCreating(true)
    try {
      let uploadedUrls: string[] = []
      // STEP 1: If files are selected, upload them and get their URLs.
      if (selectedFiles.length > 0) {
        const uploadFormData = new FormData()
        selectedFiles.forEach(file => {
          uploadFormData.append('files', file)
        })
        // Use mutateAsync to get a Promise that resolves to the upload result.
        const data = await rawUpload({
          formData: uploadFormData,
          metadata: {
            PK: `ORG_${formData?.orgId}`,
            SK: `TASK_${formData?.name?.replace(/\s/g, '_') || 'TEMP'}`,
          },
        })
        uploadedUrls = data.urls
      }
      const now = new Date().toISOString()
      const taskType = handleFormatTypesString(formData.type)
      const taskDays = calculateTaskDays(taskType) || 0

      let dateDue = ''
      let endDate = ''

      if (formData.startDate && taskDays > 0) {
        const dueDate = workday(formData.startDate, taskDays)
        dateDue = dueDate ? formatDateString(dueDate) : ''
        endDate = dueDate ? (toCalendarDate(dueDate) ?? '') : ''
      }
      // STEP 2: Build submitData with the returned image URLs.
      const submitData = {
        ...formData,
        imageUrls: uploadedUrls,
        type: formData?.type || 'Creative Intake',
        taskDays,
        dateDue,
        endDate,
        createdTime: now,
        channel: 'Creative',
        isCreative: ['website'].includes(formData?.type) ? false : true,
        isWebsite: ['website'].includes(formData?.type) ? true : false,
        isCommandCenter: true,
        parentFolderId: formData?.creativeTasksFolderId || '',
        // selectedSubtasks: selectedSubtaskKeys,
        selectedSubtasks: selectedSubtaskKeys.map(key => ({
          key,
          description: subtaskDescriptions[key] || '',
        })),
      }

      console.log('submitData', submitData)
      // STEP 3: Create the task using your submitNewTask function.
      await submitNewTask({
        formData: submitData,
        setIsCreating,
        onClose,
        parentFolderId: formData?.creativeTasksFolderId || '',
      })

      // Clear the form and file selection on success.
      setFormData({ ...initialFormData, requestType: '' })
      setSelectedFiles([])
    } catch (error) {
      console.error('❌ Error in task creation flow:', error)
      setIsCreating(false)
    }
  }
  const handleImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files.length > 0) {
      const newFiles = Array.from(event.target.files)
      // Append new files to previously selected ones
      setSelectedFiles(prevFiles => [...prevFiles, ...newFiles])
    }
  }

  const handleRemoveNewFile = (index: number) => {
    setSelectedFiles(prevFiles => prevFiles.filter((_, i) => i !== index))
  }
  if (isUsersLoading) {
    return <DrawerLoading />
  }
  return (
    <Dialog
      fullWidth
      maxWidth="md"
      PaperProps={{
        sx: {
          ...formDialogStyles({ theme, disabled: !selectedOrganization, value: selectedOrganization }),
          bgcolor: theme.palette.mode === 'dark' ? 'rgba(0,0,0,1)' : '',
        },
      }}
      open={open}
      onClose={onClose}
    >
      <CreateTaskHeader context={'Project Task'} />

      {step === 'form' ? (
        <>
          <BoxScroller>
            <DialogContent>
              <DialogContent>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, maxWidth: 600, margin: 'auto' }}>
                  {/* <Typography variant='h5' sx={{ color: theme.palette.text.primary }}>
    Creative Intake Form
  </Typography> */}

                  <Grid container rowSpacing={2} columnSpacing={1} sx={{ alignItems: 'center' }}>
                    <Grid item xs={6} sm={6} md={6} lg={6} xl={6}>
                      <FormAutocomplete
                        data={organizations}
                        isLoading={false}
                        label={'Organizations'}
                        error={creativeIntakeErrors?.orgId?.value}
                        onChange={handleOrganizationChange}
                        value={selectedOrganization as any}
                        disabled={false}
                      />
                    </Grid>
                    <Grid item xs={6} sm={6} md={6} lg={6} xl={6}>
                      <TextField
                        select
                        label="Type"
                        name="type"
                        value={formData.type || ''}
                        onChange={handleRequestTypeChange as any}
                        fullWidth
                        disabled={!selectedOrganization}
                        SelectProps={{
                          multiple: false, // ✅ Ensure it's single-select
                          renderValue: selected => creativeTaskTypeLabels[selected as keyof CreativeTypeOptions] || '',
                        }}
                        sx={{
                          '& .MuiInputBase-root': {
                            maxHeight: '56px',
                          },
                        }}
                      >
                        {Object.entries(creativeTaskTypeLabels).map(([key, label]) => (
                          <MenuItem key={key} value={key}>
                            {label}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Grid>
                    <Grid item xs={6} sm={6} md={6} lg={6} xl={6}>
                      <FormTypeSelect
                        label="Requested By"
                        value={formData.requestedBy ?? ''} // Bind to formData.type
                        options={requestedByOptions} // Pass taskTypeList
                        onChange={selectedValue => {
                          setFormData(prev => ({
                            ...prev,
                            requestedBy: selectedValue, // Update formData with selected type
                          }))
                        }}
                        disabled={!selectedOrganization}
                      />
                    </Grid>
                    <Grid item xs={6} sm={6} md={6} lg={6} xl={6}>
                      <FormTypeSelect
                        label="Request Type"
                        value={formData.requestType ?? ''} // Bind to formData.type
                        options={requestTypeOptions} // Pass taskTypeList
                        onChange={selectedValue => {
                          setFormData(prev => ({
                            ...prev,
                            requestType: selectedValue, // Update formData with selected type
                          }))
                        }}
                        disabled={!selectedOrganization}
                      />
                    </Grid>
                    <Grid item xs={6} sm={6} md={6} lg={6} xl={6}>
                      <TextField
                        label="Name"
                        name="name"
                        value={formData.name || ''}
                        onChange={handleInputChange}
                        fullWidth
                        error={!selectedOrganization ? false : creativeIntakeErrors?.name?.value}
                        InputProps={{
                          readOnly: true,
                          endAdornment: <LockIcon sx={{ color: theme.palette.text.secondary }} />,
                        }}
                        sx={{
                          '& .MuiInputBase-root': {
                            maxHeight: '56px',
                          },
                        }}
                      />
                    </Grid>
                    <Grid item xs={6} sm={6} md={6} lg={6} xl={6}>
                      <TextField
                        label="Website"
                        name="website"
                        value={formData.website}
                        onChange={handleInputChange}
                        fullWidth
                        error={!selectedOrganization ? false : creativeIntakeErrors?.website?.value}
                        sx={{
                          '& .MuiInputBase-root': {
                            maxHeight: '56px',
                          },
                        }}
                      />
                    </Grid>

                    <Grid item xs={6} sm={6} md={6} lg={6} xl={6}>
                      <OnboardTeamAutocomplete
                        users={users}
                        role="Assignees"
                        label="Assignee(s)"
                        onChange={handleUserChange}
                        defaultValue={formData?.assignees || []}
                        disabled={false}
                        isLoading={isTaskAssigneesLoading || isUsersLoading}
                        // error={marketingFormErrors?.assignee?.value}
                      />
                    </Grid>
                    <Grid item xs={6} sm={6} md={6} lg={6} xl={6}>
                      <OnboardTeamAutocomplete
                        users={users}
                        role="Assigner"
                        label="Assigner"
                        onChange={handleUserChange}
                        defaultValue={formData?.assigner ? [formData?.assigner] : []}
                        disabled={true}
                        isLoading={isActiveUserLoading || isCurrentUserLoading}

                        // error={marketingFormErrors?.assigner?.value}
                      />
                    </Grid>
                    <Grid
                      item
                      xs={9} // Set full width for textareas
                      sm={9}
                      md={9}
                      lg={9}
                      xl={9}
                    >
                      <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <DateRangePicker
                            localeText={{ start: 'Start Date', end: 'Due Date' }}
                            value={[
                              formData?.startDate ? dayjs(formData?.startDate) : null,
                              formData?.endDate ? dayjs(formData?.endDate) : null,
                            ]}
                            onChange={newValue => {
                              const [startDate, endDate] = newValue

                              setFormData(prev => ({
                                ...prev,
                                startDate: normalizeDate(startDate, 'America/New_York'),
                                endDate: normalizeDate(endDate, 'America/New_York'),
                              }))
                            }}
                            slotProps={{
                              textField: {
                                error: !selectedOrganization
                                  ? false
                                  : creativeIntakeErrors?.startDate?.value || creativeIntakeErrors.endDate?.value,
                              },
                            }}
                            sx={{
                              width: '100%',
                              '& .MuiMultiInputDateRangeField-separator': {
                                m: 0,
                              },
                              '& .MuiFormControl-root': {
                                m: 0,
                              },
                              '& .MuiInputBase-root': {
                                bgcolor: !selectedOrganization ? theme.palette.background.disabled : '',
                                maxHeight: '56px',
                                m: 0,
                              },
                            }}
                            disabled={!selectedOrganization}
                          />
                        </Box>
                      </LocalizationProvider>
                    </Grid>

                    <Grid item xs={3} sm={3} md={3} lg={3} xl={3}>
                      <TextField
                        select
                        label="Existing Client?"
                        name="existingClient"
                        value={formData.existingClient}
                        onChange={handleInputChange}
                        fullWidth
                        sx={{
                          '& .MuiInputBase-root': {
                            maxHeight: '56px',
                          },
                        }}
                      >
                        {existingClientOptions.map(option => (
                          <MenuItem key={option?.key} value={String(option?.value)}>
                            {option?.label}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Grid>
                  </Grid>
                  {formData.type && (
                    <Grid container rowSpacing={2} columnSpacing={1} sx={{ alignItems: 'center' }}>
                      <Grid item xs={12}>
                        <Typography
                          variant="h6"
                          sx={{
                            fontWeight: 'bold',
                            borderBottom: '2px solid',
                            borderColor: theme.palette.primary.main,
                          }}
                        >
                          {creativeTaskTypeLabels[formData.type as keyof CreativeTypeOptions]}
                        </Typography>
                      </Grid>

                      {fields.map(([key, config]) => (
                        <Grid key={key} item xs={6}>
                          <FieldComponent name={key} value={formData[key] || ''} onChange={handleChange} {...config} />
                        </Grid>
                      ))}
                    </Grid>
                  )}

                  <Grid
                    item
                    xs={12} // Set full width for textareas
                    sm={12}
                    md={12}
                    lg={12}
                    xl={12}
                    pt={1}
                  >
                    <TaskRichText content={formData.addtlNotes} onContentChange={handleAddtlNotesChange} />
                  </Grid>
                </Box>
              </DialogContent>
            </DialogContent>
          </BoxScroller>

          <DialogActions sx={{ px: 3, pb: 3 }}>
            <Grid container spacing={2}>
              <Grid item xs={4.5}>
                {selectedFiles.length > 0 && (
                  <NewFileAttachments
                    selectedFiles={selectedFiles.map((file, i) => ({
                      id: file.name + i,
                      name: file.name,
                      mimeType: file.type,
                      previewUrl: URL.createObjectURL(file),
                      file,
                    }))}
                    onRemoveFile={handleRemoveNewFile}
                    size={28}
                  />
                )}
              </Grid>
              <Grid item xs={3.0}>
                <Button variant="outlined" component="label">
                  Attach Files
                  <input
                    type="file"
                    accept="image/*,application/pdf,.doc,.docx"
                    multiple
                    hidden
                    onChange={e => {
                      handleImageUpload(e)
                      e.target.value = ''
                    }}
                  />
                </Button>
              </Grid>
              <Grid item xs={2}>
                <Button
                  sx={{
                    backgroundColor: 'rgb(244, 67, 54, 0.2)',
                    '&:hover': {
                      backgroundColor: 'rgb(244, 67, 54, 0.7)',
                    },
                  }}
                  onClick={onClose}
                  variant="outlined"
                >
                  Cancel
                </Button>
              </Grid>
              <Grid item xs={2}>
                <Button
                  color="secondary"
                  variant="contained"
                  onClick={() => {
                    if (isFormValid) setStep('subtasks')
                  }}
                  disabled={!isFormValid || isCreating}
                >
                  Next
                </Button>
              </Grid>
            </Grid>
          </DialogActions>
        </>
      ) : (
        <SubtaskStep
          subtaskOptions={subtaskOptions}
          selectedSubtaskKeys={selectedSubtaskKeys}
          setSelectedSubtaskKeys={setSelectedSubtaskKeys}
          subtaskDescriptions={subtaskDescriptions}
          setSubtaskDescriptions={setSubtaskDescriptions}
          onBack={() => setStep('form')}
          onSubmit={handleSubmitNewTask}
          isCreating={isCreating}
        />
      )}
      <Stack
        sx={{
          position: 'fixed',
          top: 75,
          left: '85%',
          transform: 'translateX(-70%)',
          backgroundColor: theme.palette.background.input,
          color: 'white',
          padding: '8px 16px',
          borderRadius: 2,
          boxShadow: 6,
          zIndex: 1402, // VERY important
          animation: 'flowText 8s linear infinite',
        }}
      >
        <Typography width={'100%'}>
          This form will create a new task attached to the selected organization and will be populated in Team Tasks.
        </Typography>
        <Typography sx={{ color: theme.palette.text.warning, width: '100%' }}>
          -- To create tasks attached to a specific onboard or project:
        </Typography>
        <Typography>visit Projects or NCO and click the row name to display the drawer</Typography>
      </Stack>
    </Dialog>
  )
}

export default CreativeIntakeForm
